import { useMemo } from "react";
import { GridDataTableColumn } from "../GridDataTable/GridDataTableColumn";

/**
 * Single, module-scoped Cell renderer for boolean columns.
 *
 * Because the renderer is pure and does not close over any per-column
 * data, one shared reference is sufficient — this avoids creating a new
 * function identity for every boolean column on each `useMemo`
 * recomputation.
 */
export const BooleanCell = ({ value }: { value: boolean | string | null | undefined }) => {
  if (value === null || value === undefined || value === "") return "";
  return String(value).toLowerCase() === "true" ? "True" : "False";
};

/**
 * Options accepted by `useGridBooleanCell`.
 */
export interface UseGridBooleanCellOptions {
  /** The column definitions that may contain boolean-typed columns. */
  columns: GridDataTableColumn[];
}

/**
 * React hook that returns a **new** array of `GridDataTableColumn`
 * definitions where every column with `type: 'boolean'` has a `Cell`
 * renderer that formats the raw boolean value as `"True"` or `"False"`.
 *
 * The original column objects are **not** mutated — the hook produces
 * shallow copies with the `Cell` property set.
 *
 * The result is memoized via `useMemo` and only recomputes when the
 * `columns` reference changes.
 *
 * @example
 * ```tsx
 * const columns: GridDataTableColumn[] = [
 *   {
 *     Header: 'Is Active',
 *     accessor: 'isActive',
 *     type: 'boolean',
 *     width: 150,
 *   },
 * ];
 *
 * const mappedColumns = useGridBooleanCell({ columns });
 *
 * const tableState = useDataTable({
 *   initialColumns: mappedColumns,
 *   // ...
 * });
 * ```
 */
export function useGridBooleanCell({
  columns,
}: UseGridBooleanCellOptions): GridDataTableColumn[] {
  return useMemo(() => {
    return columns.map((column) => {
      if (column.type !== "boolean") {
        return column;
      }

      return {
        ...column,
        Cell: BooleanCell,
      };
    });
  }, [columns]);
}
